import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  Autocomplete,
  Checkbox,
  Chip,
  TextField,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { SectionWrapper } from "../../../../UI/SectionWrapper/SectionWrapper";
import { AssetAccordionDetails } from "../../../ui-components/pageLayout/TableSettingsComponents";
import { useFormikContext } from "formik";
import { cmsService } from "@client/http/cms.service";
import {
  getMediaCollectionsSelector,
  GetCollectionsAction,
} from "../../../store/reducers/cmsReducer";
import type { CMSCollection } from "@client/models/CMS/cmsCollection";

interface Props {
  assetId: string;
  readonly?: boolean;
  refreshKey?: number;
}

const EntitlementsAccordion: React.FC<Props> = ({
  assetId,
  readonly,
  refreshKey,
}) => {
  const dispatch = useDispatch();
  const allCollections = useSelector(getMediaCollectionsSelector);
  const { setFieldValue } = useFormikContext<any>();
  const [loaded, setLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Load all collections for the autocomplete dropdown
  useEffect(() => {
    if (allCollections.length === 0) {
      dispatch(GetCollectionsAction());
    }
  }, [dispatch, allCollections.length]);

  // Load current asset membership into local state and sync to Formik for form submit
  useEffect(() => {
    cmsService
      .getAssetCollections(assetId)
      .toPromise()
      .then((collections) => {
        const ids = ((collections as CMSCollection[]) || []).map((c) => c._id);
        setSelectedIds(ids);
        setFieldValue("entitlementCollectionIds", ids);
        setLoaded(true);
      })
      .catch(() => {
        setSelectedIds([]);
        setFieldValue("entitlementCollectionIds", []);
        setLoaded(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, refreshKey]);

  return (
    <SectionWrapper>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          className="accordion-summary-gray"
        >
          Entitlements
          {loaded && ` (${selectedIds.length})`}
        </AccordionSummary>
        <AssetAccordionDetails>
          {readonly ? (
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#f3f4f6",
                borderLeft: "4px solid #3b82f6",
              }}
            >
              Read-only: Catchup assets inherit entitlements from their source
              channel. Edit the source channel to modify.
            </div>
          ) : (
            <Autocomplete
              multiple
              options={allCollections}
              disableCloseOnSelect
              getOptionLabel={(option) => option.name || option._id}
              value={allCollections.filter((c) => selectedIds.includes(c._id))}
              onChange={(_event, newValue) => {
                const ids = newValue.map((c) => c._id);
                setSelectedIds(ids);
                setFieldValue("entitlementCollectionIds", ids);
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.name || option._id}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name || option._id}
                    size="small"
                    {...getTagProps({ index })}
                    key={option._id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Media Collections"
                  placeholder="Add collection..."
                />
              )}
            />
          )}
        </AssetAccordionDetails>
      </Accordion>
    </SectionWrapper>
  );
};

export default EntitlementsAccordion;
